import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react';

/** Natural size of each state; the viewport clamp may shrink both. */
const PILL = { w: 340, h: 72 };
const PANEL = { w: 380, h: 560 };
/** Breathing room the frame always keeps from the viewport edges. */
const EDGE = 16;
/** Resting gap at the bottom-right corner, before the user drags the frame. */
const CORNER = 24;
/** Under this width the panel drops its fixed width and spans the viewport. */
const NARROW = 720;
/**
 * Crossfade pílula ⇄ painel (só opacidade; o tamanho troca de uma vez); zerado
 * quando a pessoa pede movimento reduzido.
 */
const CROSSFADE_MS = 180;

/**
 * Movimento (px) a partir do qual o gesto deixa de ser clique e vira arraste.
 * Vive aqui, e é exportado, porque os dois lados do mesmo gesto precisam do MESMO
 * número: o quadro decide com ele quando começar a andar, e o launcher decide com
 * ele se descarta o clique do fim do gesto. Limiares divergentes reabririam o
 * defeito que este valor existe para fechar — o quadro andando num clique que
 * ainda abre o painel.
 */
export const DRAG_SLOP = 4;

interface Size {
  w: number;
  h: number;
}

interface Point {
  x: number;
  y: number;
}

interface Viewport {
  vw: number;
  vh: number;
}

function readViewport(): Viewport {
  return { vw: window.innerWidth, vh: window.innerHeight };
}

/** Pill or panel, already reduced to what the viewport can hold. */
function frameSize(open: boolean, { vw, vh }: Viewport): Size {
  const roomW = Math.max(vw - 2 * EDGE, 0);
  const roomH = Math.max(vh - 2 * EDGE, 0);
  if (!open) return { w: Math.min(PILL.w, roomW), h: Math.min(PILL.h, roomH) };
  return { w: vw < NARROW ? roomW : Math.min(PANEL.w, roomW), h: Math.min(PANEL.h, roomH) };
}

/** Keep the whole frame on screen; on tiny viewports the edge margin wins. */
function clampToViewport(at: Point, size: Size, { vw, vh }: Viewport): Point {
  return {
    x: Math.max(EDGE, Math.min(at.x, vw - size.w - EDGE)),
    y: Math.max(EDGE, Math.min(at.y, vh - size.h - EDGE)),
  };
}

/** Where the frame rests until someone drags it: the bottom-right corner. */
function restingPoint(size: Size, { vw, vh }: Viewport): Point {
  return { x: vw - size.w - CORNER, y: vh - size.h - CORNER };
}

export interface ShibaFrame {
  /** The frame element (pill or panel) — also the boundary of "focus is inside". */
  rootRef: RefObject<HTMLDivElement>;
  /** Position, size and crossfade duration, as custom properties. */
  style: CSSProperties;
  dragging: boolean;
  /** Spread on every surface allowed to drag the frame. */
  gripProps: { onPointerDown: (event: ReactPointerEvent) => void };
}

/**
 * Geometry of the floating Shiba frame: it is the single source of the pill and
 * panel dimensions, of the drag position, and of the crossfade duration — the CSS
 * only reads the custom properties this hook publishes. Sizes are clamped to the
 * viewport (and the panel goes near-full-width on narrow screens), so the widget
 * can never push the page into horizontal scroll.
 */
export function useShibaFrame(open: boolean, reducedMotion: boolean): ShibaFrame {
  const rootRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState<Viewport>(readViewport);
  /** `null` until the first drag: the frame follows the resting corner. */
  const [at, setAt] = useState<Point | null>(null);
  const [dragging, setDragging] = useState(false);

  const size = frameSize(open, viewport);

  useEffect(() => {
    const onResize = (): void => setViewport(readViewport());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // The frame grows and shrinks from its bottom-right corner, so a pill the user
  // parked somewhere expands into a panel without sliding out from under them.
  const sizeRef = useRef(size);
  useLayoutEffect(() => {
    const previous = sizeRef.current;
    sizeRef.current = size;
    if (previous.w === size.w && previous.h === size.h) return;
    setAt((current) =>
      current === null
        ? null
        : { x: current.x + previous.w - size.w, y: current.y + previous.h - size.h },
    );
    // `size` is derived from these two numbers; depending on the object would
    // re-run the anchor shift on every render.
  }, [size.w, size.h]);

  const position = clampToViewport(at ?? restingPoint(size, viewport), size, viewport);

  // A drag in flight when the widget unmounts must not keep listening.
  const stopDragRef = useRef<() => void>();
  useEffect(() => () => stopDragRef.current?.(), []);

  const onPointerDown = (event: ReactPointerEvent): void => {
    if (event.button !== 0) return;
    // Arrastar move o quadro, não rabisca a página: sem isto o pointerdown ainda
    // produz o mousedown de compatibilidade que INICIA uma seleção, e o gesto
    // pinta de azul tudo por onde passa. Os controles (ação da pílula, recolher)
    // param a propagação antes daqui, então clique e foco continuam intactos.
    event.preventDefault();
    const origin = position;
    const startX = event.clientX;
    const startY = event.clientY;
    // Absent in jsdom, and merely an optimisation in the browser.
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDragging(true);

    // O quadro só começa a andar depois do limiar. Sem isso o tremor de 1-2px de
    // um clique comum já preenchia `at` — e `at` preenchido tira `restingPoint`
    // do caminho para sempre: o widget parava de acompanhar o canto inferior
    // direito e, no primeiro resize, ficava estacionado no meio do conteúdo.
    let moving = false;
    const onMove = (move: MouseEvent): void => {
      const dx = move.clientX - startX;
      const dy = move.clientY - startY;
      if (!moving && Math.abs(dx) <= DRAG_SLOP && Math.abs(dy) <= DRAG_SLOP) return;
      moving = true;
      setAt(clampToViewport({ x: origin.x + dx, y: origin.y + dy }, size, viewport));
    };
    const stop = (): void => {
      setDragging(false);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
      stopDragRef.current = undefined;
    };
    stopDragRef.current = stop;
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
  };

  const style = {
    '--shiba-x': `${position.x}px`,
    '--shiba-y': `${position.y}px`,
    '--shiba-w': `${size.w}px`,
    '--shiba-h': `${size.h}px`,
    '--shiba-dur': reducedMotion ? '0ms' : `${CROSSFADE_MS}ms`,
  } as CSSProperties;

  return { rootRef, style, dragging, gripProps: { onPointerDown } };
}
